/**
 * Session Messages Store
 *
 * Manages the persistent chat log for live prioritization sessions.
 *
 * Transport strategy:
 *   1. Supabase Realtime (postgres_changes) — instant delivery for live sessions
 *   2. Polling fallback at 5 s intervals if the Realtime channel fails to connect
 *      (e.g. network restrictions, Supabase plan limits)
 *
 * Public API (startPolling / stopPolling) is unchanged so SessionChatPanel and
 * SessionDetail require no edits.
 */

import { create } from 'zustand';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSafeSupabaseClient, generateUUID } from '../../lib/supabaseSync';
import type { SessionMessage } from '../../types/prioritization/session';

const TABLE_NAME = 'session_messages';
const FALLBACK_POLL_INTERVAL_MS = 5000;

function rowToMessage(row: Record<string, unknown>): SessionMessage {
  return {
    id: row.id as string,
    sessionId: row.session_id as string,
    workspaceId: row.workspace_id as string,
    userId: row.user_id as string,
    userName: (row.user_name as string) || '',
    body: row.body as string,
    createdAt: row.created_at != null ? new Date(row.created_at as string) : new Date(0),
  };
}

interface SessionMessagesState {
  messages: SessionMessage[];
  isSending: boolean;
  error: string | null;
  isRealtimeConnected: boolean;

  /** Internal tracking */
  _activeSessionId: string | null;
  _channel: RealtimeChannel | null;
  _pollingHandle: ReturnType<typeof setInterval> | null;
  _lastFetchedAt: Date | null;

  /** Load all messages for a session (initial fetch) */
  fetchMessages: (sessionId: string) => Promise<void>;

  /** Send a new message; optimistically appends before DB confirms */
  sendMessage: (input: {
    sessionId: string;
    workspaceId: string;
    userId: string;
    userName: string;
    body: string;
  }) => Promise<boolean>;

  /**
   * Subscribe to new messages for a session.
   * Attempts Realtime first; falls back to polling automatically.
   * Alias kept as startPolling to match existing callers.
   */
  startPolling: (sessionId: string, intervalMs?: number) => void;

  /** Unsubscribe / stop polling. */
  stopPolling: () => void;

  /** Selector: messages for a specific session, sorted oldest-first */
  getMessagesForSession: (sessionId: string) => SessionMessage[];

  /** Internal helpers */
  _startFallbackPolling: (sessionId: string, intervalMs: number) => void;
  _stopFallbackPolling: () => void;
  _appendMessages: (incoming: SessionMessage[]) => void;
}

export const useSessionMessagesStore = create<SessionMessagesState>()((set, get) => ({
  messages: [],
  isSending: false,
  error: null,
  isRealtimeConnected: false,
  _activeSessionId: null,
  _channel: null,
  _pollingHandle: null,
  _lastFetchedAt: null,

  fetchMessages: async (sessionId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) {
        if (error.code === '42P01' || error.message?.toLowerCase().includes('does not exist')) {
          return;
        }
        set({ error: error.message });
        return;
      }

      const fetched = (data || []).map(rowToMessage);
      const now = new Date();
      set(state => {
        const other = state.messages.filter(m => m.sessionId !== sessionId);
        return {
          messages: [...other, ...fetched],
          error: null,
          _lastFetchedAt: now,
        };
      });
    } catch (err) {
      set({ error: String(err) });
    }
  },

  sendMessage: async ({ sessionId, workspaceId, userId, userName, body }) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return false;

    const trimmed = body.trim();
    if (!trimmed) return false;

    const optimistic: SessionMessage = {
      id: `optimistic-${generateUUID()}`,
      sessionId,
      workspaceId,
      userId,
      userName,
      body: trimmed,
      createdAt: new Date(),
    };

    set(state => ({
      messages: [...state.messages, optimistic],
      isSending: true,
      error: null,
    }));

    try {
      const { data, error } = await supabase
        .from(TABLE_NAME)
        .insert({
          session_id: sessionId,
          workspace_id: workspaceId,
          user_id: userId,
          user_name: userName,
          body: trimmed,
        })
        .select()
        .single();

      if (error) {
        set(state => ({
          messages: state.messages.filter(m => m.id !== optimistic.id),
          isSending: false,
          error: error.message,
        }));
        return false;
      }

      const confirmed = rowToMessage(data as unknown as Record<string, unknown>);

      set(state => ({
        messages: state.messages.map(m => (m.id === optimistic.id ? confirmed : m)),
        isSending: false,
        error: null,
        _lastFetchedAt: confirmed.createdAt,
      }));

      return true;
    } catch (err) {
      set(state => ({
        messages: state.messages.filter(m => m.id !== optimistic.id),
        isSending: false,
        error: String(err),
      }));
      return false;
    }
  },

  startPolling: (sessionId, intervalMs = FALLBACK_POLL_INTERVAL_MS) => {
    const { _activeSessionId, _channel, _pollingHandle } = get();

    // Already subscribed to the same session
    if (_activeSessionId === sessionId && (_channel || _pollingHandle)) return;

    // Clean up previous subscription
    get().stopPolling();

    set({ _activeSessionId: sessionId });

    // Initial full fetch
    void get().fetchMessages(sessionId);

    const supabase = getSafeSupabaseClient();
    if (!supabase) {
      get()._startFallbackPolling(sessionId, intervalMs);
      return;
    }

    // Try Realtime first
    let realtimeConnected = false;
    const connectionTimeout = setTimeout(() => {
      if (!realtimeConnected) {
        get()._startFallbackPolling(sessionId, intervalMs);
      }
    }, 5000);

    const channel = supabase
      .channel(`session-messages:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: TABLE_NAME,
          filter: `session_id=eq.${sessionId}`,
        },
        (payload) => {
          const incoming = rowToMessage(payload.new as Record<string, unknown>);
          get()._appendMessages([incoming]);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          realtimeConnected = true;
          clearTimeout(connectionTimeout);
          set({ isRealtimeConnected: true });
          // Stop any fallback polling that may have started
          get()._stopFallbackPolling();
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          realtimeConnected = false;
          set({ isRealtimeConnected: false });
          get()._startFallbackPolling(sessionId, intervalMs);
        }
      });

    set({ _channel: channel });
  },

  stopPolling: () => {
    const { _channel, _pollingHandle } = get();
    const supabase = getSafeSupabaseClient();

    if (_channel && supabase) {
      void supabase.removeChannel(_channel);
    }
    if (_pollingHandle) {
      clearInterval(_pollingHandle);
    }

    set({
      _channel: null,
      _pollingHandle: null,
      _activeSessionId: null,
      isRealtimeConnected: false,
    });
  },

  // ── Internal helpers ───────────────────────────────────────────────────────

  _appendMessages: (incoming) => {
    if (incoming.length === 0) return;
    const now = new Date();
    set(state => {
      const existingIds = new Set(state.messages.map(m => m.id));
      // Filter out optimistic messages that have been confirmed by the server
      const novel = incoming.filter(m => !existingIds.has(m.id));
      if (novel.length === 0) return { _lastFetchedAt: now };
      return {
        messages: [...state.messages, ...novel].sort(
          (a, b) => a.createdAt.getTime() - b.createdAt.getTime()
        ),
        _lastFetchedAt: now,
      };
    });
  },

  _startFallbackPolling: (sessionId, intervalMs) => {
    get()._stopFallbackPolling();

    const handle = setInterval(async () => {
      const supabase = getSafeSupabaseClient();
      if (!supabase) return;

      const { _lastFetchedAt, _activeSessionId } = get();
      if (_activeSessionId !== sessionId) return;

      try {
        let query = supabase
          .from(TABLE_NAME)
          .select('*')
          .eq('session_id', sessionId)
          .order('created_at', { ascending: true });

        if (_lastFetchedAt) {
          query = query.gt('created_at', _lastFetchedAt.toISOString());
        }

        const { data, error } = await query;
        if (error) return;
        get()._appendMessages((data || []).map(rowToMessage));
      } catch {
        // Swallow polling errors silently
      }
    }, intervalMs);

    set({ _pollingHandle: handle });
  },

  _stopFallbackPolling: () => {
    const { _pollingHandle } = get();
    if (_pollingHandle) {
      clearInterval(_pollingHandle);
      set({ _pollingHandle: null });
    }
  },

  getMessagesForSession: (sessionId) =>
    get()
      .messages.filter(m => m.sessionId === sessionId)
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime()),
}));
